import React, { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { Loading, Typography } from "ui-library/atoms";
import { ContentBox } from "ui-library/layouts";

import i18n from "core/i18n";
import { ContinueButton, InPageFormButtonsLayout } from "containers/inPageForm";
import { Field } from "core/hookForm";
import { CancellationReason, VehicleCancellationReason } from "types";

import { SECTION_MARGIN } from "../constants";

import { AccountSection, CancellationNoteSection, ChangeDateSection, ContactPersonSection, RemovedFromRegistrationSection } from "./field";

import { FormStep, VehicleCancellationFormValues } from "../types";

interface StepOneProps {
    containsParallelCancellable: boolean;
    cancellationReasons: CancellationReason[];
    onMoveToFormStep: (formStep: FormStep) => (data) => void;
}

export const Step1CancellationParams: FC<StepOneProps> = ({ containsParallelCancellable, cancellationReasons, onMoveToFormStep }) => {
    const { t } = i18n.useTranslation();

    const { setValue, watch, handleSubmit } = useFormContext<VehicleCancellationFormValues>();

    const cancellationReasonOptions = cancellationReasons.map((reason) => ({
        code: reason.code,
        value: reason.label,
    }));

    const selectedCancellationReason = watch("cancellationReason");

    useEffect(() => {
        const cancellationReasonDefinition = cancellationReasons.find((definition) => definition.code === selectedCancellationReason);
        const defaultDate = cancellationReasonDefinition?.defaultCancellationDate;
        setValue("cancellationDate", defaultDate);
    }, [selectedCancellationReason]);

    return (
        <Loading loading={false}>
            {containsParallelCancellable && (
                <ContentBox fullWidth>
                    <Typography markDown={t("vehicle.change.contractCancellation.containsParallelCancellable")} />
                </ContentBox>
            )}

            <ContentBox fullWidth mb={SECTION_MARGIN}>
                <Field<VehicleCancellationFormValues>
                    name="cancellationReason"
                    input="select"
                    label={t("vehicle.change.contractCancellation.cancellationReason")}
                    options={cancellationReasonOptions}
                    includeEmptyOption={false}
                />
            </ContentBox>

            {selectedCancellationReason && <ChangeDateSection />}

            {selectedCancellationReason === VehicleCancellationReason.BY_AGREEMENT && <CancellationNoteSection />}

            {selectedCancellationReason === VehicleCancellationReason.REMOVED_FROM_REGISTER && <RemovedFromRegistrationSection />}

            {selectedCancellationReason === VehicleCancellationReason.INSURER_DEATH && <ContactPersonSection />}

            {selectedCancellationReason && <AccountSection />}

            <InPageFormButtonsLayout
                rightButton={
                    <ContinueButton
                        handleSubmit={handleSubmit(onMoveToFormStep(FormStep.S2_REVIEW_SUBMIT))}
                        disabled={!selectedCancellationReason}
                    />
                }
            />
        </Loading>
    );
};
